import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, Sparkles, ArrowRight, User, ShieldCheck, Mail, KeyRound } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Overline, Loading } from "@/components/shared";

export default function Account() {
  const { user, entitlements, refresh } = useAuth();
  const [products, setProducts] = useState(null);
  const [busy, setBusy] = useState(null);
  const [pref, setPref] = useState(user?.auth_preferences?.primary || "magic_link");
  const [params] = useSearchParams();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    if (window.location.hash === "#upgrade") {
      setTimeout(() => document.getElementById("upgrade")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);

  useEffect(() => {
    const intent = params.get("intent");
    if (intent && products) { buy(intent); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, products]);

  const owned = (key) => {
    if (key === "part_1") return entitlements.has_part_1;
    if (key === "part_2") return entitlements.has_part_2;
    if (key === "part_3") return entitlements.has_part_3;
    if (key === "bundle") return entitlements.bundle_owner;
    return false;
  };

  const buy = async (key) => {
    if (owned(key) || busy) return;
    setBusy(key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(null);
    }
  };

  const savePref = async (p) => {
    setPref(p);
    try { await api.put("/auth/preferences", { primary: p }); toast.success("Preference saved"); await refresh(); }
    catch { toast.error("Could not save preference"); }
  };

  if (!products) return <Loading />;

  const toneMap = { part_1: "primary", part_2: "plum", part_3: "gold", bundle: "coral" };

  return (
    <div className="max-w-4xl" data-testid="account-page">
      <Overline>Account</Overline>
      <h1 className="font-serif text-4xl sm:text-5xl text-nm-ink mt-2">Your account</h1>

      {/* Profile */}
      <div className="mt-8 grid sm:grid-cols-2 gap-6">
        <div className="rounded-3xl border border-nm-primary/12 bg-nm-surface p-7 nm-shadow-sm">
          <div className="flex items-center gap-2 text-nm-primary font-medium text-sm uppercase tracking-wide"><User size={16} /> Profile</div>
          <div className="mt-5 space-y-3">
            <Row label="Name" value={user?.name} />
            <Row label="Email" value={user?.email} />
          </div>
        </div>
        <div className="rounded-3xl border border-nm-primary/12 bg-nm-surface p-7 nm-shadow-sm">
          <div className="flex items-center gap-2 text-nm-primary font-medium text-sm uppercase tracking-wide"><ShieldCheck size={16} /> Products owned</div>
          <div className="mt-5 space-y-2.5">
            <OwnRow label="Part 1 — Foundations" owned={entitlements.has_part_1} />
            <OwnRow label="Part 2 — The Human Side" owned={entitlements.has_part_2} />
            <OwnRow label="Part 3 — The Moments" owned={entitlements.has_part_3} />
            <OwnRow label="Bundle (all three)" owned={entitlements.bundle_owner} />
          </div>
        </div>
      </div>

      {/* Login preferences */}
      <div className="mt-6 rounded-3xl border border-nm-primary/12 bg-nm-surface p-7 nm-shadow-sm">
        <div className="flex items-center gap-2 text-nm-primary font-medium text-sm uppercase tracking-wide"><KeyRound size={16} /> Login preference</div>
        <p className="text-sm text-nm-neutral mt-2">Your preferred way to sign in next time.</p>
        <div className="flex flex-wrap gap-3 mt-4">
          <PrefBtn active={pref === "magic_link"} onClick={() => savePref("magic_link")} icon={Mail} label="Magic link" testid="pref-magic" />
          <PrefBtn active={pref === "password"} onClick={() => savePref("password")} icon={KeyRound} label="Email & password" testid="pref-password" />
        </div>
      </div>

      {/* Upgrade / purchase */}
      <div id="upgrade" className="mt-10 scroll-mt-24">
        <Overline>{entitlements.bundle_owner ? "You own the full system" : "Unlock more"}</Overline>
        <h2 className="font-serif text-3xl text-nm-ink mt-2">
          {entitlements.bundle_owner ? "Everything is unlocked" : "Purchase & upgrade"}
        </h2>
        {!entitlements.bundle_owner && (entitlements.has_part_1 || entitlements.has_part_2 || entitlements.has_part_3) && (
          <p className="text-nm-neutral mt-2 max-w-2xl">
            You already own part of the system. Add the remaining toolkits below — or grab the bundle to unlock all three.
          </p>
        )}

        {entitlements.bundle_owner ? (
          <div className="mt-6 rounded-3xl border border-nm-green/25 bg-nm-green/[0.06] p-8 flex items-center gap-4">
            <div className="h-12 w-12 rounded-2xl bg-nm-green/15 text-nm-green flex items-center justify-center"><Check size={24} /></div>
            <div>
              <div className="font-serif text-2xl text-nm-ink">Full access</div>
              <p className="text-nm-neutral text-sm mt-1">Part 1, Part 2, and Part 3 are all unlocked. Enjoy the complete toolkit.</p>
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-6">
            {products.map((p, i) => {
              const isOwned = owned(p.lookup_key);
              const tone = toneMap[p.lookup_key];
              return (
                <motion.div key={p.lookup_key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                  className={`flex flex-col rounded-3xl border bg-nm-surface p-7 nm-shadow-sm ${p.lookup_key === "bundle" ? "border-nm-coral/35 ring-1 ring-nm-coral/20" : "border-nm-primary/12"}`}
                  data-testid={`product-${p.lookup_key}`}>
                  <div className="font-serif text-2xl text-nm-ink">{p.name.split(":")[0]}</div>
                  <div className="text-sm text-nm-neutral mt-1 flex-1">{p.tagline}</div>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="font-serif text-4xl text-nm-ink">${p.price}</span>
                    <span className="text-sm text-nm-neutral">one-time</span>
                  </div>
                  {isOwned ? (
                    <div className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-nm-green/12 px-5 py-3 text-sm font-medium text-nm-green" data-testid={`owned-${p.lookup_key}`}>
                      <Check size={16} /> Owned
                    </div>
                  ) : (
                    <button onClick={() => buy(p.lookup_key)} disabled={busy === p.lookup_key} data-testid={`buy-${p.lookup_key}`}
                      className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium text-white disabled:opacity-60 transition-[filter,transform] duration-200 hover:-translate-y-0.5 hover:brightness-105 ${tone === "coral" ? "bg-nm-coral" : tone === "plum" ? "bg-nm-plum" : tone === "gold" ? "bg-[#C2952B]" : "bg-nm-primary"}`}>
                      {busy === p.lookup_key ? "Redirecting…" : <><Sparkles size={15} /> Buy now <ArrowRight size={15} /></>}
                    </button>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
        {!entitlements.bundle_owner && (
          <p className="text-xs text-nm-neutral mt-5">Secure checkout via Stripe. Test card: 4242 4242 4242 4242, any future expiry, any CVC.</p>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-sm text-nm-neutral">{label}</span>
      <span className="text-sm font-medium text-nm-ink text-right">{value}</span>
    </div>
  );
}
function OwnRow({ label, owned }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm text-nm-ink">{label}</span>
      {owned ? <span className="inline-flex items-center gap-1 text-xs font-medium text-nm-green"><Check size={13} /> Owned</span>
        : <span className="text-xs text-nm-neutral/70">Locked</span>}
    </div>
  );
}
function PrefBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium border transition-colors duration-200 ${active ? "bg-nm-primary text-white border-nm-primary" : "bg-white text-nm-neutral border-nm-primary/15 hover:text-nm-ink"}`}>
      <Icon size={15} /> {label}
    </button>
  );
}
